/**
 * 🐛 **Turning what a player typed into an issue** — T174, D12.
 *
 * 🎯 **haugur, 2026-09-07:** *"we should also look into a bug reporting feature
 * on the site for them to use, allow the to report bugs with text and
 * screenshots."* 🎯 **And it lands as a Forgejo issue, automatically** — which
 * **D12** already says, so nothing new is being decided about where bugs live.
 *
 * ## 🔴 Why this is here and not beside the HTTP route
 *
 * ⚠️ **`server/index.ts` cannot be imported without a database**, so a test file
 * beside it does not run on this workstation at all. **Both of 2026-09-07's
 * faults were in seams no test could reach** — a float fed to an integer column,
 * and a message the parser hung up on. **If a decision can be arithmetic over
 * plain values, it goes where a test can see it.**
 *
 * ## 🔴 The report is DATA, and the person writing it is not trusted
 *
 * ⚠️ **Everything here arrives from a browser**, including on dev where there
 * are no accounts at all. A title goes into an issue that other people read, and
 * the context block is quoted back verbatim. **So it is bounded, trimmed and
 * fenced** — never because a family member would attack us, but because the
 * shape of the fault is *"a field nobody thought would be long"*.
 */

import { zToNorthing } from './world.ts';

/** The longest a title may be. Forgejo takes far more; a title is a line. */
export const MAX_TITLE = 80;
/** The longest the whole description may be, in characters. */
export const MAX_TEXT = 4000;
/**
 * The longest a reporter's name may be in the byline.
 *
 * ⚠️ A display name is chosen by the person who signed up, so it is bounded
 * here for the same reason every other field is: **the shape of the fault is
 * "a field nobody thought would be long"**, and this one frames a table row.
 */
export const MAX_REPORTER = 40;

/** Which door a report came in by. */
export type BugDoor = 'game' | 'website';

/** What the game knows about the moment a report was made. */
export interface BugContext {
  /** Which build. 🔴 **The single most useful field** — see below. */
  readonly build?: string;
  /** Which environment: `development`, `staging`. */
  readonly env?: string;
  /** Where the player was standing, in ISN93 metres. */
  readonly at?: { readonly x: number; readonly y: number; readonly z: number } | null;
  /** The world's own time of day when it happened. */
  readonly gameTime?: string;
  /** The browser's own words for itself. */
  readonly agent?: string;
  /** How big their window is — a layout fault is invisible without it. */
  readonly screen?: string;
  /** The protocol version the client speaks. */
  readonly protocol?: number;
  /** The last few console errors, newest last. ⚠️ Bounded by the caller. */
  readonly errors?: readonly string[];
  /**
   * ⏱️ **How the game was running when the picture was taken** — T186.
   *
   * 🎯 **A tester, 2026-09-10:** *"game runs laggy."* 🎯 **haugur:** *"i got no
   * further explanations."* 🔴 **"Laggy" hides two faults with opposite fixes**
   * — the picture stuttering on their machine, and the world arriving late over
   * the wire — **and the word does not say which.** These numbers do.
   *
   * ⚠️ **The client's own answer about the client's own machine.** Unlike the
   * byline there is nothing to verify it against and nothing resting on it: it
   * describes hardware only that browser can see. **It is a symptom, not a
   * claim about anybody.**
   */
  readonly performance?: BugPerformanceContext;
  /**
   * 🧍 **Who filed it — the display name on their account.**
   *
   * 🔴 **THE SERVER'S ANSWER, NEVER THE REPORTER'S.** The browser sends a
   * *credential* and the server looks the name up; a client that names its own
   * reporter can name somebody else's, and a byline that can be wrong is worse
   * than no byline, because it looks usable.
   *
   * 🔴 **`null` is the ordinary case on dev**, where nobody signs in. It also
   * covers a credential the server could not resolve. Both mean *we do not know
   * who this was*, and `byline` says so in words rather than inventing a name.
   */
  readonly reporter?: string | null;
  /**
   * Which door it came in by. ⚠️ **Also the server's answer**, decided by which
   * credential it was given.
   */
  readonly via?: BugDoor;
}

/**
 * ⏱️ **What the player's machine was doing** — see {@link BugContext.performance}.
 *
 * ⚠️ **Every field optional and every one printed only if present.** An older
 * client that knows nothing about these simply leaves the rows out; a row of
 * zeroes would read as a frozen game, which is a worse lie than saying nothing.
 */
export interface BugPerformanceContext {
  /** Smoothed frames per second — the steady state, and what the HUD shows. */
  readonly fps?: number;
  /** 🔴 The longest single frame in the last second. **This is the stutter.** */
  readonly worstFrameMs?: number;
  /** How many frames in that second were slow enough to feel. */
  readonly hitches?: number;
  /**
   * 🔴 **The worst single frame in the last HALF-MINUTE** — and this is the
   * number a bug report is actually for.
   *
   * 🎯 **haugur, 2026-09-17:** *"when i fly to the shore my framerate drops to 2
   * to 5 fps as the worl blocks are beeing drawn."*
   *
   * ⚠️ **His report said `60 fps` and it was not lying.** The other numbers here
   * describe **one second** — and he pressed F8 *after landing*, once the world
   * had settled. In that second there genuinely were no slow frames.
   *
   * > 🔴 **NOBODY PRESSES F8 DURING THE STALL. THEY PRESS IT AFTER THEY
   * > NOTICE.**
   *
   * **So the report described the aftermath and not the fault**, and it took him
   * re-testing by hand to find what this row should have said by itself.
   */
  readonly rememberedWorstMs?: number;
  /** How many slow frames in that half-minute. */
  readonly rememberedHitches?: number;
  /** Render distance in chunks — the first thing to try when it feels heavy. */
  readonly viewRadius?: number;
  /** Chunks asked for and not yet arrived. */
  readonly pending?: number;
  /** Median wait from asking for a chunk to it arriving, in milliseconds. */
  readonly chunkWaitMs?: number;
  /** The worst such wait in the window. */
  readonly chunkWorstMs?: number;
  /** Chunk meshes in the scene — roughly one draw call each. */
  readonly meshes?: number;
}

/** What goes to Forgejo. */
export interface BugIssue {
  readonly title: string;
  readonly body: string;
}

/**
 * 🐛 **One report, as an issue.**
 *
 * 🔴 **THE CONTEXT IS THE POINT, NOT THE PROSE.** *"It looked wrong"* with the
 * build, the coordinates, the window size and the last console error is a
 * report somebody can act on; a paragraph without them is a conversation.
 * **A website form cannot collect any of it**, which is why the in-game key
 * exists.
 *
 * 🔴 **THE BYLINE IS DECIDED HERE AND NOWHERE ELSE.** There are two doors into
 * this function — the key in the game and the form on the website — and until
 * 2026-09-10 they wrote *who* two different ways: the game wrote nothing at all,
 * and the website smuggled a name into the **Browser** row as *"from the
 * website, by Maggi"*. ⚠️ *Two ways in that forget what the other does* is the
 * fault this repo has recorded five times. **One row, one rule, one sentence for
 * not knowing.**
 *
 * ⚠️ **The build sha leads the technical fields because of a specific
 * afternoon**: on 2026-09-07 a fault was reported that had already been fixed
 * and deployed, and the first question — *which build were you on?* — could not
 * be answered by anybody. **Who filed it sits above them**, because with four
 * accounts on staging it had to be worked out from the clock.
 *
 * @param text What the player typed. Its **first line becomes the title**;
 *   that is how people write anyway, and it saves asking twice.
 */
export function bugIssue(text: string, context: BugContext): BugIssue {
  const clean = text.replace(/\r/g, '').trim().slice(0, MAX_TEXT);
  const [first = '', ...rest] = clean.split('\n');
  /* ⚠️ An empty report is still an issue. Somebody pressed the key and meant
     something by it, and a refusal here loses the screenshot too. */
  const headline = first.trim() || 'A bug, with no words';
  const title = headline.slice(0, MAX_TITLE);
  /**
   * 🔴 **THE REPORTER'S OWN WORDS ARE NEVER THROWN AWAY. THEY USED TO BE.**
   *
   * 🎯 **haugur, 2026-09-17, on issue #662:** *"did you see the note about them
   * swimming flat"* — **and the answer was no, because this function had
   * deleted it.**
   *
   * ⚠️ **What it did:** the title was `first.slice(0, MAX_TITLE)` and the body
   * was everything after the FIRST NEWLINE. **He typed one long line**, so
   * there was no "after" — and **every character past the eightieth was
   * silently destroyed.** The issue read *"…but sometimes insted of swim"* and
   * stopped there.
   *
   * 🔴 **This is the most valuable instrument this project owns** — the handoff
   * says so in as many words — **and it was quietly losing the one part of a
   * report that a machine cannot reconstruct.** Everything else in the issue is
   * generated from the game; the sentence is the only thing that is his.
   *
   * ✅ **So when the headline did not fit, the WHOLE text goes in the body.** A
   * short report is unchanged; a long one repeats its first eighty characters
   * and keeps the rest. 👉 **Repeating a line costs nothing. Losing one costs a
   * bug nobody can act on.**
   */
  const cut = headline.length > MAX_TITLE;
  const body = (cut ? clean : rest.join('\n')).trim();

  const lines: string[] = [];
  if (body) lines.push(body, '');
  else if (clean) lines.push('', '');
  lines.push('| | |', '|---|---|');
  const row = (name: string, value: string | undefined): void => {
    if (value === undefined || value === '') return;
    /* 🔴 Pipes would break the table and a newline would end it. The value is
       a player's browser string or a console error — neither is ours. */
    lines.push(`| **${name}** | ${value.replace(/\|/g, '\\|').replace(/\n/g, ' ')} |`);
  };
  /* 🧍 Always printed, and never empty — an unsigned report is what this row
     exists to end. `byline` answers even when nobody can be named. */
  row('Reported by', byline(context));
  row('Build', context.build);
  row('Where', context.env);
  row(
    'Standing at',
    /**
     * 🔴 **THE NORTHING, NOT THE WORLD'S OWN z.**
     *
     * 🎯 **Found by haugur's first real report, #529**, which said he was
     * standing at **−396323** — **not a place in Iceland.**
     *
     * ⚠️ **The world is right-handed, so its z is the NEGATED ISN93 northing**
     * (`zToNorthing`). The HUD converts; `/api/roads` converts and says in its
     * own comment that it *"speaks northings"*; **this printed the raw internal
     * number.**
     *
     * 🔴 **A coordinate is the field that makes a report actionable** — it is
     * how somebody goes and stands where the fault was. **A sign error makes it
     * worse than absent**, because it looks usable.
     */
    context.at
      ? `${round(context.at.x)}, ${round(zToNorthing(context.at.z))} · height ${round(context.at.y)}`
      : undefined,
  );
  row('Game time', context.gameTime);
  row('Window', context.screen);
  row('Protocol', context.protocol === undefined ? undefined : String(context.protocol));
  row('Browser', context.agent);

  /**
   * ⏱️ **Two rows, because they are two different faults** — T186.
   *
   * 🔴 **Splitting them here is the whole point of the ticket.** *Laggy* was one
   * word covering *my picture stutters* (their machine) and *the world arrives
   * late* (the wire and the server), **which have opposite fixes.** A report
   * that carries both numbers says which one the player meant without anybody
   * having to ask them a follow-up question they may never answer.
   *
   * ⚠️ **The worst frame is printed beside the average, never instead of it.**
   * An average alone is what hid this for weeks — 🎯 *"my hud s telling me that
   * im at 60fps"* while it stuttered — and an average is still the right
   * number for *is this machine coping at all*.
   */
  row('Drawing', drawingRow(context.performance));
  row('World arriving', arrivingRow(context.performance));

  if (context.errors && context.errors.length > 0) {
    lines.push('', '**The console said:**', '', '```');
    /* ⚠️ Fenced, and a fence inside an error would end the block early. */
    for (const e of context.errors) lines.push(e.replace(/```/g, "'''"));
    lines.push('```');
  }

  lines.push(
    '',
    '---',
    '',
    context.via === 'website'
      ? '🐛 *Reported from the website.*'
      : '🐛 *Reported from inside the game.*',
  );
  return { title, body: lines.join('\n') };
}

/**
 * ⏱️ **What the picture cost** — frames, and how bad the worst one was.
 *
 * Returns `undefined` when there is nothing to say, so `row` leaves the line
 * out entirely. ⚠️ **A row of zeroes would read as a frozen game.**
 */
function drawingRow(p: BugPerformanceContext | undefined): string | undefined {
  if (p === undefined || p.fps === undefined) return undefined;
  const parts = [`${round(p.fps)} fps`];
  /* 🔴 The number the average cannot see. Printed only when there IS one, so a
     smooth report stays short and a stuttering one is obvious at a glance. */
  if (p.worstFrameMs !== undefined && p.hitches !== undefined && p.hitches > 0) {
    parts.push(
      `worst frame ${round(p.worstFrameMs)} ms`,
      `${String(p.hitches)} slow in the second`,
    );
  }
  /**
   * 🔴 **WHAT JUST HAPPENED, not what is happening.**
   *
   * ⚠️ **Printed whenever the half-minute holds a stall, EVEN IF THE SECOND IS
   * PERFECT** — which is the whole point, because that is exactly the case that
   * lost haugur's stutter. **A row reading `60 fps · worst frame 480 ms in the
   * last 30 s` is not a contradiction; it is the report doing its job.**
   */
  if (
    p.rememberedHitches !== undefined &&
    p.rememberedHitches > 0 &&
    p.rememberedWorstMs !== undefined
  ) {
    parts.push(
      `worst ${round(p.rememberedWorstMs)} ms in the last 30 s`,
      `${String(p.rememberedHitches)} slow frames in it`,
    );
  }
  if (p.meshes !== undefined) parts.push(`${String(p.meshes)} chunk meshes drawn`);
  if (p.viewRadius !== undefined) parts.push(`view ${String(p.viewRadius * 32)} m`);
  return parts.join(' · ');
}

/**
 * ⏱️ **What the world cost** — how long a chunk took from asking to arriving.
 *
 * 🔴 **Not a ping.** This is *ask for ground → stand on ground*, which includes
 * the server generating it — and on 2026-09-15 that was where nearly all of the
 * wait lived: 42 ms a chunk, one at a time, while the containers idled.
 */
function arrivingRow(p: BugPerformanceContext | undefined): string | undefined {
  if (p === undefined || p.chunkWaitMs === undefined) return undefined;
  const parts = [`${round(p.chunkWaitMs)} ms a chunk`];
  if (p.chunkWorstMs !== undefined) parts.push(`worst ${round(p.chunkWorstMs)} ms`);
  /* ⚠️ Nothing pending is worth SAYING, not worth omitting: it is the
     difference between "the world had not arrived yet" and "this is what the
     world looks like when it has", which look identical in a screenshot. */
  parts.push(
    p.pending === undefined
      ? 'still arriving, count unknown'
      : p.pending > 0
        ? `${String(p.pending)} still to come`
        : 'nothing outstanding',
  );
  return parts.join(' · ');
}

/**
 * 🧍 **Who to say filed it.**
 *
 * 🔴 **It always answers.** *"an anonymous player on development"* is a fact
 * somebody can act on — it says do not go looking for an account. **A blank row
 * is the state this whole change exists to end**, so there is no path here that
 * returns nothing.
 *
 * ⚠️ **No bold, no code marks around the name.** Inline markers do not nest in
 * Forgejo's markdown and the marks show on the page — this repo has shipped that
 * twice, in the invite letter and in a guide chapter. The name is printed as
 * the person wrote it, bounded, and `row` handles the pipes and newlines that
 * would break the table.
 */
function byline(context: BugContext): string {
  const name = context.reporter?.trim().slice(0, MAX_REPORTER) ?? '';
  if (name !== '') return name;
  /* ⚠️ The environment, when we have it, because *anonymous on development* is
     ordinary and *anonymous on staging* means a credential did not resolve —
     and those are worth telling apart at a glance. */
  return context.env === undefined || context.env === ''
    ? 'an anonymous player'
    : `an anonymous player on ${context.env}`;
}

/** Metres, to the nearest one. A player is not standing on a millimetre. */
function round(n: number): string {
  return String(Math.round(n));
}
